import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Copy, RefreshCw, LogOut, KeyRound, HardDrive } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, errMsg } from "../lib/api";
import { clearDefaults, loadDefaults } from "../lib/storage";

const LABELS = {
  fuel_price: "Fuel price", loaded_mpg: "Loaded MPG", empty_mpg: "Empty MPG", maintenance_cpm: "Maintenance CPM",
  driver_mode: "Driver pay mode", driver_flat: "Flat pay", driver_rate: "Hourly rate", driver_hours: "Hours",
};

const ApiKeyCard = () => {
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/settings/api-key").then((r) => setKey(r.data.api_key)).catch((e) => toast.error(errMsg(e))); }, []);
  const regen = async () => {
    if (!window.confirm("Regenerate key? The current key will stop working immediately.")) return;
    setBusy(true);
    try { setKey((await api.post("/settings/api-key/regenerate")).data.api_key); toast.success("New API key generated"); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const copy = () => navigator.clipboard?.writeText(key).then(() => toast.success("Copied"), () => toast.error("Copy failed"));
  const base = process.env.REACT_APP_BACKEND_URL;
  return (
    <section className="card">
      <div className="card-head"><KeyRound size={16} /><h2>Integration API Key</h2></div>
      <p className="muted">Read-only access to your saved results (no cost inputs) for dispatch tools & apps.</p>
      <code className="keybox" data-testid="text-api-key">{key || "…"}</code>
      <div className="row-btns">
        <button className="btn ghost" data-testid="btn-copy-api-key" onClick={copy}><Copy size={16} /> Copy</button>
        <button className="btn ghost" data-testid="btn-regenerate-api-key" disabled={busy} onClick={regen}><RefreshCw size={16} className={busy ? "spin" : ""} /> Regenerate</button>
      </div>
      <pre className="snippet" data-testid="api-usage-snippet">{`curl -H "X-API-Key: <key>" \\\n  ${base}/api/integration/results`}</pre>
    </section>
  );
};

const DefaultsCard = ({ userId }) => {
  const [d, setD] = useState(() => loadDefaults(userId));
  const entries = useMemo(() => (d ? Object.entries(LABELS).filter(([k]) => d[k] !== undefined && d[k] !== "") : []), [d]);
  return (
    <section className="card" data-testid="card-user-defaults">
      <div className="card-head"><HardDrive size={16} /><h2>My Defaults (this device)</h2></div>
      {!d ? <p className="muted" data-testid="defaults-empty">No defaults saved. Use "Save as my defaults" on the calculator.</p> : (
        <>
          <dl className="kv">
            {entries.map(([k, l]) => (
              <div key={k}><dt>{l}</dt><dd data-testid={`default-${k}`}>{d[k]}</dd></div>
            ))}
          </dl>
          <button className="btn ghost" data-testid="btn-clear-defaults" onClick={() => { clearDefaults(userId); setD(null); toast.success("Defaults cleared"); }}>Clear defaults</button>
        </>
      )}
    </section>
  );
};

export default function SettingsPage() {
  const { user, logout } = useAuth();
  return (
    <div className="narrow" data-testid="settings-page">
      <h1 className="page-title">Settings</h1>
      <p className="page-sub" data-testid="text-user-email">{user.email}</p>
      <ApiKeyCard />
      <DefaultsCard userId={user.id} />
      <button className="btn danger" data-testid="btn-logout" onClick={logout}><LogOut size={16} /> Log out</button>
    </div>
  );
}
